import React, { createContext, useState, useEffect, useRef } from "react";
import { account } from "../appwrite";

interface User {
  id: string;
  email: string;
  name: string;
  prefs?: any;
}

interface AssessmentData {
  [key: string]: any;
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  assessmentData: AssessmentData | null;
  hasPaid: boolean;
  hasCompletedAssessment: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  saveAssessment: (data: AssessmentData) => void;
  getAssessment: () => AssessmentData | null;
  syncAssessmentToAppwrite: () => Promise<void>;
  markAsPaid: (packageId: string) => Promise<void>;
  markAssessmentComplete: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  pollForSession: (maxRetries?: number, delayMs?: number) => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(
    null,
  );
  const [hasPaid, setHasPaid] = useState(false);
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
  const hasCheckedAuth = useRef(false);

  // Check if user is already logged in on mount
  useEffect(() => {
    // Prevent running twice in StrictMode development
    if (hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;

    const checkAuth = async () => {
      try {
        const session = await account.get();
        if (session) {
          console.log("✅ User authenticated:", session.email);
          setUser({
            id: session.$id,
            email: session.email,
            name: session.name,
            prefs: session.prefs,
          });
          // Check if user has paid
          const hasPaidPref = session.prefs?.hasPaid === "true";
          setHasPaid(hasPaidPref);
          // Check if user has completed assessment
          const assessmentCompletePref =
            session.prefs?.assessmentCompleted === "true";
          setHasCompletedAssessment(assessmentCompletePref);
        }
      } catch (error) {
        console.log("No active session:", (error as any).message);
        setUser(null);
        setHasPaid(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Load assessment data from localStorage
    const savedAssessment = localStorage.getItem("fireup_assessment");
    if (savedAssessment) {
      try {
        setAssessmentData(JSON.parse(savedAssessment));
      } catch (error) {
        console.error("Failed to parse saved assessment:", error);
      }
    }
  }, []);

  const loginWithGoogle = async () => {
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      // This will redirect the user to Google, and after they authenticate,
      // Appwrite will redirect back to the callback URL with a session cookie
      account.createOAuth2Session({
        provider: "google" as any,
        success: redirectUrl,
        failure: redirectUrl, // failure URL (same for now)
      });
      // Note: The page will redirect, so this code may not execute
    } catch (error) {
      console.error("Google login failed:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await account.deleteSession({ sessionId: "current" });
      setUser(null);
      setHasPaid(false);
      setHasCompletedAssessment(false);
      localStorage.removeItem("fireup_assessment");
      setAssessmentData(null);
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  };

  const saveAssessment = (data: AssessmentData) => {
    setAssessmentData(data);
    localStorage.setItem("fireup_assessment", JSON.stringify(data));
  };

  const getAssessment = () => {
    return assessmentData;
  };

  const syncAssessmentToAppwrite = async () => {
    if (!user || !assessmentData) return;

    try {
      // Update user preferences with assessment data
      await account.updatePrefs({
        prefs: {
          ...user.prefs,
          assessmentData: JSON.stringify(assessmentData),
          assessmentDate: new Date().toISOString(),
        }
      });
      setUser({
        ...user,
        prefs: {
          ...user.prefs,
          assessmentData: JSON.stringify(assessmentData),
          assessmentDate: new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error("Failed to sync assessment to Appwrite:", error);
      throw error;
    }
  };

  const markAsPaid = async (packageId: string) => {
    if (!user) return;

    try {
      await account.updatePrefs({
        prefs: {
          ...user.prefs,
          hasPaid: "true",
          paidPackage: packageId,
          paidDate: new Date().toISOString(),
        }
      });
      setHasPaid(true);
      setUser({
        ...user,
        prefs: {
          ...user.prefs,
          hasPaid: "true",
          paidPackage: packageId,
          paidDate: new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error("Failed to mark as paid:", error);
      throw error;
    }
  };

  const markAssessmentComplete = async () => {
    if (!user) return;

    try {
      await account.updatePrefs({
        prefs: {
          ...user.prefs,
          assessmentCompleted: "true",
          assessmentCompletedAt: new Date().toISOString(),
        }
      });
      setHasCompletedAssessment(true);
      setUser({
        ...user,
        prefs: {
          ...user.prefs,
          assessmentCompleted: "true",
          assessmentCompletedAt: new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error("Failed to mark assessment as complete:", error);
      throw error;
    }
  };

  const refreshAuth = async () => {
    try {
      const session = await account.get();
      if (session) {
        console.log("✅ Auth refreshed for:", session.email);
        setUser({
          id: session.$id,
          email: session.email,
          name: session.name,
          prefs: session.prefs,
        });
        const hasPaidPref = session.prefs?.hasPaid === "true";
        setHasPaid(hasPaidPref);
        const assessmentCompletePref =
          session.prefs?.assessmentCompleted === "true";
        setHasCompletedAssessment(assessmentCompletePref);
        setIsLoading(false);
        return true;
      }
      return false;
    } catch (error) {
      console.log("No active session after refresh:", (error as any).message);
      setUser(null);
      setHasPaid(false);
      setIsLoading(false);
      return false;
    }
  };

  // Helper to poll for session with retries
  const pollForSession = async (maxRetries = 5, delayMs = 500) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const session = await account.get();
        if (session && session.$id) {
          console.log("✅ Session found:", session.email);
          setUser({
            id: session.$id,
            email: session.email,
            name: session.name,
            prefs: session.prefs,
          });
          const hasPaidPref = session.prefs?.hasPaid === "true";
          setHasPaid(hasPaidPref);
          const assessmentCompletePref =
            session.prefs?.assessmentCompleted === "true";
          setHasCompletedAssessment(assessmentCompletePref);
          return true;
        }
      } catch (error) {
        console.log(`Attempt ${i + 1}/${maxRetries}: Session not ready yet`);
      }

      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    console.log("❌ Session not found after retries");
    return false;
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    assessmentData,
    hasPaid,
    hasCompletedAssessment,
    loginWithGoogle,
    logout,
    saveAssessment,
    getAssessment,
    syncAssessmentToAppwrite,
    markAsPaid,
    markAssessmentComplete,
    refreshAuth: async () => {
      await refreshAuth();
    },
    pollForSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
